import { useEffect, useMemo, useRef, useState } from "react";
import { AlertCircle, Check, Clapperboard, Download, ExternalLink, Image as ImageIcon, Loader2, RefreshCw, Sparkles, Wrench } from "lucide-react";
import type { ViewerClient } from "../../client/ViewerClient";
import type { CameraView } from "../../shared/api-contract";
import { Modal } from "../../shared/ui";
import {
  getGoldsmithJob,
  getGoldsmithStatus,
  listGoldsmithJobs,
  listGoldsmithThemes,
  sendToGoldsmith,
  FIXER_CUTS,
  type FixerCut,
  type FixerFunction,
  type GoldsmithFeature,
  type GoldsmithJobSummary,
  type GoldsmithJobView,
  type GoldsmithRender,
  type GoldsmithStatus,
  type GoldsmithTheme,
} from "./goldsmithApi";

/**
 * Send the finished piece to TheGoldsmith — AI Photoshoot, AI Short Video, and
 * (once their partner API offers it) Jewelry Fixer.
 *
 * What goes over is a render the user can SEE before sending: the dialog
 * snapshots the current view on open, and the same capture path produces the
 * full-resolution image at send time. A video's end frame is captured by
 * flying to a saved camera view (or a preset), shooting, and flying back to a
 * temporary view of where the user was — their framing is never lost.
 */

type Tab = GoldsmithFeature | "fixer";
type Phase = "setup" | "capturing" | "sending" | "running" | "done" | "error";

const TABS: { id: Tab; label: string; icon: typeof ImageIcon; blurb: string }[] = [
  { id: "photoshoot", label: "AI Photoshoot", icon: ImageIcon, blurb: "A catalogue-ready still in a styled scene." },
  { id: "video", label: "AI Short Video", icon: Clapperboard, blurb: "A few seconds of motion from your render." },
  { id: "fixer", label: "Jewelry Fixer", icon: Wrench, blurb: "Swap stones, recolour metal, clean the background." },
];

const PHOTO_ASPECTS = ["1:1", "4:5", "3:4", "2:3", "16:9", "9:16"];

const FIXER_OPS: { id: FixerFunction; label: string; hint: string }[] = [
  { id: "swap-diamonds", label: "Swap diamonds", hint: "Re-render the stones as the cuts you choose." },
  { id: "metal-color", label: "Change metal colour", hint: "Re-tone the metal to the style's colour." },
  { id: "clean-background", label: "Clean background", hint: "Put the piece on a clean studio backdrop." },
];
const cutLabel = (c: string) => c.charAt(0).toUpperCase() + c.slice(1);

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

async function blobToBase64(b: Blob): Promise<string> {
  const buf = new Uint8Array(await b.arrayBuffer());
  let s = "";
  for (let i = 0; i < buf.length; i += 0x8000) s += String.fromCharCode(...buf.subarray(i, i + 0x8000));
  return btoa(s);
}

function uid(): string {
  return (crypto.randomUUID?.() ?? `${Date.now()}-${Math.random().toString(36).slice(2)}`).replace(/[^A-Za-z0-9_-]/g, "");
}

function price(theme: GoldsmithTheme | undefined, duration: number): string {
  if (!theme || theme.credits == null) return "price unknown";
  if (theme.creditsUnit === "second") return `${+(theme.credits * duration).toFixed(2)} credits (${theme.credits}/sec)`;
  return `${theme.credits} credits`;
}

export function SendToGoldsmithDialog({
  open,
  onClose,
  client,
  category = "rings",
}: {
  open: boolean;
  onClose: () => void;
  client: ViewerClient | null;
  category?: string;
}) {
  const [status, setStatus] = useState<GoldsmithStatus | null>(null);
  const [statusError, setStatusError] = useState<string | null>(null);
  const [tab, setTab] = useState<Tab>("photoshoot");
  // Keyed by feature, and by operation for Jewelry Fixer ("production:swap-diamonds").
  const [themes, setThemes] = useState<Record<string, GoldsmithTheme[]>>({});
  const [fixerFn, setFixerFn] = useState<FixerFunction>("swap-diamonds");
  const [cuts, setCuts] = useState<FixerCut[]>(["round"]);
  const [stoneCount, setStoneCount] = useState("");
  const [themeId, setThemeId] = useState<string | null>(null);
  const [prompt, setPrompt] = useState("");
  const [aspect, setAspect] = useState("1:1");
  const [resolution, setResolution] = useState<string | null>(null);
  const [duration, setDuration] = useState(5);
  const [endAt, setEndAt] = useState<string>("none");
  const [views, setViews] = useState<CameraView[]>([]);
  const [preview, setPreview] = useState<string | null>(null);
  const [phase, setPhase] = useState<Phase>("setup");
  const [error, setError] = useState<string | null>(null);
  const [job, setJob] = useState<GoldsmithJobView | null>(null);
  const [recent, setRecent] = useState<GoldsmithJobSummary[]>([]);
  const alive = useRef(true);
  const previewRef = useRef<string | null>(null);
  previewRef.current = preview;
  useEffect(() => () => { if (previewRef.current) URL.revokeObjectURL(previewRef.current); }, []);

  useEffect(() => {
    alive.current = open;
    if (!open) return;
    setPhase("setup");
    setJob(null);
    setError(null);
    let signedIn = false;
    try { signedIn = !!localStorage.getItem("auth_token"); } catch { /* private mode */ }
    if (!signedIn) setStatusError("Sign in to your Jewelitt account to send pieces to TheGoldsmith.");
    else getGoldsmithStatus()
      .then((s) => { setStatus(s); setStatusError(null); })
      .catch((e: Error) => setStatusError(e.message));
    listGoldsmithJobs().then(setRecent).catch(() => undefined);
    if (client) {
      void Promise.resolve(client.ring.listCameraViews()).then((v) => setViews((v ?? []).filter((x) => !x.name.startsWith("__goldsmith"))));
      void refreshPreview();
    }
    return () => { alive.current = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Fixer runs only once TheGoldsmith offers it to this key; until then the tab says "Soon".
  const feature: GoldsmithFeature | null = tab === "fixer" ? (status?.fixerAvailable ? "production" : null) : tab;
  const themeKey = feature === "production" ? `production:${fixerFn}` : feature;
  const list = themeKey ? themes[themeKey] : undefined;

  useEffect(() => {
    if (!open || !status?.connected || !feature || !themeKey || themes[themeKey]) return;
    listGoldsmithThemes(feature, category, feature === "production" ? fixerFn : undefined)
      .then((t) => setThemes((m) => ({ ...m, [themeKey]: t })))
      .catch((e: Error) => setError(e.message));
  }, [open, status?.connected, feature, themeKey, fixerFn, themes, category]);

  const theme = useMemo(() => list?.find((t) => t.themeId === themeId) ?? list?.[0], [list, themeId]);

  // Keep the option pickers inside what the chosen theme accepts.
  useEffect(() => {
    if (!theme) return;
    if (feature === "video") {
      const d = theme.video?.durations;
      if (d?.length && !d.includes(duration)) setDuration(theme.video?.defaultDuration ?? d[0]);
      const ar = theme.video?.aspectRatios;
      if (ar?.length && !ar.includes(aspect)) setAspect(ar[0]);
      if (!theme.video?.supportsLastImage) setEndAt("none");
      setResolution(theme.video?.resolutions?.[0] ?? null);
    } else {
      setResolution(theme.options?.resolutions?.[0] ?? null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [theme?.themeId, feature]);

  const frameAspect = () => {
    const r = document.querySelector("main iframe")?.getBoundingClientRect();
    return r && r.width > 0 && r.height > 0 ? r.height / r.width : 1;
  };

  /** The current view, as the viewer renders it for export. */
  const capture = async (longEdge: number): Promise<GoldsmithRender> => {
    if (!client) throw new Error("The viewer is not ready yet.");
    const a = frameAspect();
    const w = a <= 1 ? longEdge : Math.round(longEdge / a);
    const h = a <= 1 ? Math.round(longEdge * a) : longEdge;
    let blob = await client.ring.snapshot({ width: w, height: h, format: "png", frames: 48 });
    // Keep well inside the 8 MB per-render limit: a busy scene can make a 2k PNG heavy.
    if (blob.size > 6.5 * 1024 * 1024) blob = await client.ring.snapshot({ width: w, height: h, format: "jpeg", quality: 0.93, frames: 48 });
    return { mediaType: blob.type === "image/jpeg" ? "image/jpeg" : "image/png", data: await blobToBase64(blob) };
  };

  async function refreshPreview() {
    if (!client) return;
    try {
      const small = await client.ring.snapshot({ width: 640, height: Math.round(640 * frameAspect()), format: "jpeg", quality: 0.85, frames: 24 });
      const url = URL.createObjectURL(small);
      setPreview((old) => { if (old) URL.revokeObjectURL(old); return url; });
    } catch {
      /* preview is a nicety */
    }
  }

  /** Shoot from another angle, then put the camera back exactly where it was. */
  const captureAt = async (target: string): Promise<GoldsmithRender> => {
    const ring = client!.ring;
    const back = await ring.addCameraView("__goldsmith_return");
    try {
      if (target === "top" || target === "front") {
        await ring.setCameraPreset(target);
        await sleep(900);
      } else {
        await ring.flyToCameraView(target.slice(5), 0);
        await sleep(250);
      }
      return await capture(1600);
    } finally {
      await Promise.resolve(ring.flyToCameraView(back.id, 0)).catch(() => undefined);
      await Promise.resolve(ring.deleteCameraView(back.id)).catch(() => undefined);
    }
  };

  const send = async () => {
    if (!feature || !theme) return;
    setError(null);
    try {
      setPhase("capturing");
      const images = [await capture(1600)];
      const endFrame = feature === "video" && endAt !== "none" ? await captureAt(endAt) : undefined;
      setPhase("sending");
      const params: Record<string, unknown> = {};
      if (feature === "production") {
        params.functionType = fixerFn;
        if (fixerFn === "swap-diamonds") {
          if (cuts.length) params.diamonds = cuts.map((cut) => ({ cut }));
          const n = Number(stoneCount);
          if (stoneCount.trim() && Number.isInteger(n) && n > 0) params.diamondCount = Math.min(999, n);
        }
        if (resolution) params.resolution = resolution;
      } else if (feature === "photoshoot") {
        params.aspectRatio = aspect;
        if (resolution) params.resolution = resolution;
      } else {
        params.duration = duration;
        if (theme.video?.aspectRatios?.length) params.aspectRatio = aspect;
        if (resolution) params.resolution = resolution;
      }
      const created = await sendToGoldsmith({
        feature,
        images,
        ...(endFrame ? { endFrame } : {}),
        themeId: theme.themeId,
        themeName: theme.name,
        ...(prompt.trim() ? { prompt: prompt.trim() } : {}),
        params,
        idempotencyKey: uid(),
      });
      setPhase("running");
      await poll(created.jobId);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Sending failed.");
      setPhase("error");
    }
  };

  const poll = async (jobId: string) => {
    for (let i = 0; i < 400 && alive.current; i++) {
      const j = await getGoldsmithJob(jobId);
      if (!alive.current) return;
      setJob(j);
      if (j.status === "completed" && j.savedToVault) { setPhase("done"); listGoldsmithJobs().then(setRecent).catch(() => undefined); return; }
      if (j.status === "failed" || j.status === "cancelled") { setError(j.error ?? "TheGoldsmith could not finish this job."); setPhase("error"); return; }
      await sleep(i < 10 ? 3000 : 6000);
    }
  };

  const busy = phase === "capturing" || phase === "sending" || phase === "running";
  const notConnected = status && !status.connected;
  const allowed = (f: GoldsmithFeature) => !status?.features || status.features.includes(f);

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="xl"
      staticBehavior={busy}
      title={<span className="flex items-center gap-2"><Sparkles size={16} className="text-gold-500" /> Send to TheGoldsmith</span>}
      description="Turn this piece into a styled photo or a short video. Results are saved to your Vault."
    >
      <div data-goldsmith-dialog>
        {statusError ? (
          <Notice tone="error">{statusError}</Notice>
        ) : !status ? (
          <div className="flex items-center gap-2 py-8 text-[13px] text-ink-500"><Loader2 size={14} className="animate-spin" /> Checking your connection…</div>
        ) : notConnected ? (
          <div className="py-4" data-goldsmith-connect-first>
            <Notice tone="info">
              Connect your TheGoldsmith account first. Paste your API key in <b>Settings → Integrations</b> — it takes a minute, and your work here stays open.
            </Notice>
            <div className="mt-3 flex gap-2">
              <a
                href="/settings?tab=integrations"
                target="_blank"
                rel="noreferrer"
                className="flex items-center gap-1.5 rounded-lg gold-gradient px-3.5 py-2 text-[13px] font-semibold text-white"
              >
                Open Settings <ExternalLink size={13} />
              </a>
              <button
                onClick={() => getGoldsmithStatus().then(setStatus).catch(() => undefined)}
                className="flex items-center gap-1.5 rounded-lg border border-ink-200 px-3.5 py-2 text-[13px] text-ink-600 hover:border-gold-400"
              >
                <RefreshCw size={13} /> I’ve connected it
              </button>
            </div>
          </div>
        ) : (
          <>
            {/* Tabs */}
            <div className="mb-4 flex gap-1.5 rounded-xl bg-ink-50 p-1">
              {TABS.map((t) => {
                const soon = t.id === "fixer" && !status.fixerAvailable;
                const denied = t.id !== "fixer" && !allowed(t.id);
                const on = tab === t.id;
                return (
                  <button
                    key={t.id}
                    onClick={() => !busy && setTab(t.id)}
                    disabled={busy}
                    className={`flex flex-1 items-center justify-center gap-1.5 rounded-lg px-3 py-2 text-[12.5px] font-medium transition ${
                      on ? "bg-white text-ink-900 shadow-sm" : "text-ink-500 hover:text-ink-800"
                    }`}
                    data-goldsmith-tab={t.id}
                  >
                    <t.icon size={14} />
                    {t.label}
                    {soon ? <span className="rounded-full bg-ink-200 px-1.5 text-[10px] font-semibold uppercase text-ink-500">Soon</span> : null}
                    {denied ? <span className="rounded-full bg-rose/10 px-1.5 text-[10px] font-semibold uppercase text-rose">Not on key</span> : null}
                  </button>
                );
              })}
            </div>

            {tab === "fixer" && !status.fixerAvailable ? (
              <Notice tone="info">
                Jewelry Fixer (swap stones, recolour metal, clean background) is on its way. TheGoldsmith has to open it to partner
                API keys first — it will switch on here automatically when they do.
              </Notice>
            ) : phase === "running" || phase === "done" ? (
              <JobProgress job={job} phase={phase} onAnother={() => { setPhase("setup"); setJob(null); void refreshPreview(); }} />
            ) : (
              <div className="grid grid-cols-[240px_1fr] gap-5">
                {/* What we send */}
                <div>
                  <div className="mb-1.5 flex items-center justify-between text-[12px] font-semibold text-ink-700">
                    What we’ll send
                    <button onClick={() => void refreshPreview()} className="flex items-center gap-1 text-[11px] font-normal text-ink-400 hover:text-ink-700" title="Re-capture the current view">
                      <RefreshCw size={11} /> Refresh
                    </button>
                  </div>
                  <div className="overflow-hidden rounded-xl border border-ink-100 bg-ink-50">
                    {preview ? <img src={preview} alt="Current view" className="w-full" data-goldsmith-preview /> : <div className="flex aspect-square items-center justify-center text-ink-300"><Loader2 size={16} className="animate-spin" /></div>}
                  </div>
                  <p className="mt-1.5 text-[11px] leading-snug text-ink-400">
                    Your current view, rendered at full quality when you send. Frame the piece the way you want it first.
                  </p>
                  {feature === "video" && theme?.video?.supportsLastImage ? (
                    <label className="mt-3 block text-[12px] text-ink-600">
                      End on
                      <select value={endAt} onChange={(e) => setEndAt(e.target.value)} className="mt-1 w-full rounded-lg border border-ink-200 px-2 py-1.5 text-[12.5px]">
                        <option value="none">Same view (let the AI move)</option>
                        <option value="top">Top view</option>
                        <option value="front">Front view</option>
                        {views.map((v) => <option key={v.id} value={`view:${v.id}`}>Saved view: {v.name}</option>)}
                      </select>
                    </label>
                  ) : null}
                </div>

                {/* Style + options */}
                <div className="min-w-0">
                  {feature === "production" ? (
                    <div className="mb-3" data-goldsmith-fixer>
                      <div className="mb-1.5 text-[12px] font-semibold text-ink-700">What should it fix?</div>
                      <div className="grid grid-cols-3 gap-1.5">
                        {FIXER_OPS.map((op) => (
                          <button
                            key={op.id}
                            onClick={() => { setFixerFn(op.id); setThemeId(null); }}
                            title={op.hint}
                            className={`rounded-lg border px-2 py-1.5 text-left text-[12px] transition ${
                              fixerFn === op.id ? "border-gold-500 bg-gold-50 text-ink-900 ring-2 ring-gold-400/30" : "border-ink-100 text-ink-600 hover:border-ink-300"
                            }`}
                            data-goldsmith-fixer-op={op.id}
                          >
                            <div className="font-medium">{op.label}</div>
                            <div className="text-[10.5px] leading-snug text-ink-400">{op.hint}</div>
                          </button>
                        ))}
                      </div>
                      {fixerFn === "swap-diamonds" ? (
                        <div className="mt-3">
                          <div className="mb-1 text-[12px] text-ink-600">Stones in this piece</div>
                          <div className="flex flex-wrap gap-1.5">
                            {FIXER_CUTS.map((c) => {
                              const on = cuts.includes(c);
                              return (
                                <button
                                  key={c}
                                  onClick={() => setCuts((cs) => (on ? cs.filter((x) => x !== c) : FIXER_CUTS.filter((x) => x === c || cs.includes(x))))}
                                  aria-pressed={on}
                                  className={`rounded-full border px-2 py-0.5 text-[11px] ${on ? "border-gold-500 bg-gold-50 text-ink-900" : "border-ink-200 text-ink-500 hover:border-ink-400"}`}
                                  data-goldsmith-cut={c}
                                >
                                  {cutLabel(c)}
                                </button>
                              );
                            })}
                          </div>
                          <label className="mt-2 flex items-center gap-2 text-[12px] text-ink-600">
                            How many stones
                            <input
                              value={stoneCount}
                              onChange={(e) => setStoneCount(e.target.value.replace(/[^0-9]/g, "").slice(0, 3))}
                              inputMode="numeric"
                              placeholder="optional"
                              className="w-20 rounded-lg border border-ink-200 px-2 py-1 text-[12.5px] focus:border-gold-400 focus:outline-none"
                              data-goldsmith-count
                            />
                          </label>
                        </div>
                      ) : null}
                    </div>
                  ) : null}
                  <div className="mb-1.5 text-[12px] font-semibold text-ink-700">Style</div>
                  {!list ? (
                    <div className="flex items-center gap-2 py-6 text-[12.5px] text-ink-500"><Loader2 size={14} className="animate-spin" /> Loading styles…</div>
                  ) : list.length === 0 ? (
                    <Notice tone="info">No styles are available for this key yet.</Notice>
                  ) : (
                    <div className="grid max-h-[260px] grid-cols-3 gap-2 overflow-y-auto pr-1">
                      {list.map((t) => {
                        const on = theme?.themeId === t.themeId;
                        return (
                          <button
                            key={t.themeId}
                            onClick={() => setThemeId(t.themeId)}
                            className={`overflow-hidden rounded-lg border text-left transition ${on ? "border-gold-500 ring-2 ring-gold-400/40" : "border-ink-100 hover:border-ink-300"}`}
                            data-goldsmith-theme={t.themeId}
                          >
                            {t.previewUrl ? <img src={t.previewUrl} alt="" loading="lazy" className="aspect-[4/3] w-full object-cover" /> : <div className="aspect-[4/3] bg-ink-50" />}
                            <div className="px-2 py-1.5">
                              <div className="truncate text-[12px] font-medium text-ink-900">{t.name}</div>
                              <div className="truncate text-[10.5px] text-ink-400">
                                {t.video?.engine ? `${t.video.engine} · ` : ""}
                                {t.credits == null ? "price unknown" : t.creditsUnit === "second" ? `${t.credits}/sec` : `${t.credits} credits`}
                              </div>
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  )}

                  <div className="mt-3 flex flex-wrap gap-2">
                    {feature === "photoshoot" || theme?.video?.aspectRatios?.length ? (
                      <Picker label="Shape" value={aspect} onChange={setAspect} options={feature === "video" ? theme?.video?.aspectRatios ?? [] : PHOTO_ASPECTS} />
                    ) : null}
                    {(feature === "photoshoot" ? theme?.options?.resolutions : theme?.video?.resolutions)?.length ? (
                      <Picker
                        label="Resolution"
                        value={resolution ?? ""}
                        onChange={setResolution}
                        options={(feature === "photoshoot" ? theme?.options?.resolutions : theme?.video?.resolutions) ?? []}
                      />
                    ) : null}
                    {feature === "video" && theme?.video?.durations?.length ? (
                      <Picker label="Length" value={String(duration)} onChange={(v) => setDuration(Number(v))} options={theme.video.durations.map(String)} suffix="s" />
                    ) : null}
                  </div>

                  <textarea
                    value={prompt}
                    onChange={(e) => setPrompt(e.target.value.slice(0, 1000))}
                    rows={2}
                    placeholder="Optional direction, e.g. warm window light, marble surface"
                    className="mt-3 w-full resize-none rounded-lg border border-ink-200 px-3 py-2 text-[12.5px] focus:border-gold-400 focus:outline-none"
                  />

                  {error ? <div className="mt-2"><Notice tone="error">{error}</Notice></div> : null}

                  <div className="mt-3 flex items-center justify-between gap-3">
                    <span className="text-[12px] text-ink-500">
                      {theme ? <>Cost: <b className="text-ink-800">{price(theme, duration)}</b> from your TheGoldsmith credits</> : null}
                    </span>
                    <button
                      onClick={() => void send()}
                      disabled={!theme || busy || !client || !allowed(feature!)}
                      className="flex items-center gap-1.5 rounded-lg gold-gradient px-4 py-2 text-[13px] font-semibold text-white disabled:opacity-50"
                      data-goldsmith-send
                    >
                      {busy ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
                      {phase === "capturing" ? "Rendering…" : phase === "sending" ? "Uploading…" : "Send to TheGoldsmith"}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {recent.length > 0 && phase === "setup" && tab !== "fixer" ? (
              <div className="mt-5 border-t border-ink-100 pt-3">
                <div className="mb-2 text-[12px] font-semibold text-ink-700">Recent</div>
                <div className="flex gap-2 overflow-x-auto pb-1">
                  {recent.slice(0, 8).map((r) => (
                    <button
                      key={r.jobId}
                      onClick={() => { setPhase("running"); void poll(r.jobId); }}
                      title={`${r.themeName ?? r.feature} · ${r.status}`}
                      className="w-24 shrink-0 overflow-hidden rounded-lg border border-ink-100 text-left hover:border-ink-300"
                    >
                      {r.outputs[0] && r.outputs[0].mimeType.startsWith("image/") ? (
                        <img src={r.outputs[0].url} alt="" loading="lazy" className="aspect-square w-full object-cover" />
                      ) : (
                        <div className="flex aspect-square items-center justify-center bg-ink-50 text-ink-300">
                          {r.feature === "video" ? <Clapperboard size={16} /> : <ImageIcon size={16} />}
                        </div>
                      )}
                      <div className="truncate px-1.5 py-1 text-[10.5px] text-ink-500">{r.status === "completed" ? r.themeName ?? r.feature : r.status}</div>
                    </button>
                  ))}
                </div>
              </div>
            ) : null}
          </>
        )}
      </div>
    </Modal>
  );
}

function JobProgress({ job, phase, onAnother }: { job: GoldsmithJobView | null; phase: Phase; onAnother: () => void }) {
  if (phase === "done" && job) {
    return (
      <div data-goldsmith-done>
        <div className="mb-3 flex items-center gap-2 text-[13px] font-semibold text-emerald">
          <Check size={16} /> Done — saved to your Vault{job.creditsCharged != null ? ` · ${job.creditsCharged} credits` : ""}
        </div>
        <div className="grid grid-cols-2 gap-3">
          {job.outputs.map((o, i) => (
            <div key={o.url} className="overflow-hidden rounded-xl border border-ink-100">
              {o.mimeType.startsWith("video/") ? (
                <video src={o.url} controls playsInline className="w-full" />
              ) : (
                <img src={o.url} alt={`Result ${i + 1}`} className="w-full" />
              )}
              <a href={o.url} download className="flex items-center justify-center gap-1.5 border-t border-ink-100 py-2 text-[12px] text-ink-600 hover:bg-ink-50">
                <Download size={13} /> Download
              </a>
            </div>
          ))}
        </div>
        <button onClick={onAnother} className="mt-4 rounded-lg border border-ink-200 px-3.5 py-2 text-[13px] text-ink-700 hover:border-gold-400">
          Send another
        </button>
      </div>
    );
  }
  const pct = Math.max(4, Math.min(100, job?.percent ?? 4));
  return (
    <div className="py-4" data-goldsmith-running>
      <div className="flex items-center gap-2 text-[13px] font-medium text-ink-800">
        <Loader2 size={15} className="animate-spin text-gold-500" />
        {job?.status === "completed" ? "Saving to your Vault…" : job?.status === "processing" ? "TheGoldsmith is working on it…" : "Queued at TheGoldsmith…"}
      </div>
      <div className="mt-3 h-1.5 overflow-hidden rounded-full bg-ink-100">
        <div className="h-full rounded-full bg-gold-400 transition-all" style={{ width: `${pct}%` }} />
      </div>
      {job?.steps?.length ? (
        <ul className="mt-3 space-y-1 text-[12px] text-ink-500">
          {job.steps.map((s) => (
            <li key={s.label} className="flex items-center gap-1.5">
              {s.status === "completed" ? <Check size={12} className="text-emerald" /> : s.status === "running" ? <Loader2 size={12} className="animate-spin" /> : <span className="h-1.5 w-1.5 rounded-full bg-ink-200" />}
              {s.label}
            </li>
          ))}
        </ul>
      ) : null}
      <p className="mt-3 text-[11.5px] text-ink-400">
        Photos usually take under a minute; videos a few minutes. If you close this, open Send to TheGoldsmith again and pick it from Recent.
      </p>
    </div>
  );
}

function Picker({ label, value, onChange, options, suffix = "" }: { label: string; value: string; onChange: (v: string) => void; options: string[]; suffix?: string }) {
  return (
    <label className="flex items-center gap-1.5 rounded-lg border border-ink-200 px-2 py-1 text-[12px] text-ink-500">
      {label}
      <select value={value} onChange={(e) => onChange(e.target.value)} className="bg-transparent text-[12.5px] text-ink-900 outline-none">
        {options.map((o) => <option key={o} value={o}>{o}{suffix}</option>)}
      </select>
    </label>
  );
}

function Notice({ tone, children }: { tone: "info" | "error"; children: React.ReactNode }) {
  return (
    <div className={`flex items-start gap-2 rounded-lg border px-3 py-2.5 text-[12.5px] ${tone === "error" ? "border-rose/30 bg-rose/5 text-rose" : "border-ink-100 bg-ink-50 text-ink-600"}`}>
      <AlertCircle size={14} className="mt-0.5 shrink-0" />
      <span>{children}</span>
    </div>
  );
}
